import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingBag, MapPin, Palette } from 'lucide-react';
import { Button } from '../ui/button';
import { useCart } from '../../context/CartContext';
import { useTheme } from '../../context/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';

const navLinks = [
  { href: '/menu', label: 'Menú' },
  { href: '/ubicaciones', label: 'Ubicaciones' },
  { href: '/catering', label: 'Catering' },
];

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { itemCount, toggleCart, selectedLocation } = useCart();
  const { cycleTheme, currentThemeData } = useTheme();
  const location = useLocation();

  return (
    <header className="sticky top-0 z-[100] bg-background/95 backdrop-blur-lg border-b border-border/50">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="font-heading text-2xl md:text-3xl font-medium tracking-tight"
            data-testid="header-logo"
          >
            Lumière
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map(link => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm tracking-wide transition-colors hover:text-foreground ${
                  location.pathname === link.href 
                    ? 'text-foreground font-medium' 
                    : 'text-muted-foreground'
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* Location indicator */}
            {selectedLocation && (
              <Link 
                to="/ubicaciones"
                className="hidden md:flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="selected-location-indicator"
              >
                <MapPin className="w-4 h-4 text-accent" />
                <span className="max-w-[120px] truncate">{selectedLocation.name}</span>
              </Link>
            )}

            {/* Theme Switcher */}
            <button
              onClick={cycleTheme}
              className="hidden md:flex items-center justify-center w-10 h-10 rounded-full hover:bg-secondary transition-colors"
              title={`Tema: ${currentThemeData.name}`}
              data-testid="theme-switcher"
            >
              <Palette className="w-5 h-5 text-accent" />
            </button>

            {/* Cart Button - Desktop */}
            <Button
              onClick={toggleCart}
              variant="outline"
              className="hidden md:flex items-center gap-2 h-10 px-4 rounded-full border-border/60 hover:bg-secondary"
              data-testid="cart-button-desktop"
            >
              <ShoppingBag className="w-5 h-5" />
              <AnimatePresence mode="wait">
                {itemCount > 0 && (
                  <motion.span
                    key={itemCount}
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.5, opacity: 0 }}
                    className="bg-accent text-accent-foreground text-xs font-medium w-5 h-5 rounded-full flex items-center justify-center"
                  >
                    {itemCount}
                  </motion.span>
                )}
              </AnimatePresence>
            </Button>

            {/* CTA - Desktop */}
            <Link to="/menu" className="hidden md:block">
              <Button 
                className="h-10 px-6 bg-primary text-primary-foreground rounded-full font-medium hover:bg-primary/90 transition-all"
                data-testid="order-now-btn"
              >
                Pedir ahora
              </Button>
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 hover:bg-secondary rounded-lg transition-colors"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-border/50 bg-background"
          >
            <nav className="px-6 py-4 space-y-1">
              {navLinks.map(link => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-3 text-lg transition-colors ${
                    location.pathname === link.href 
                      ? 'text-foreground font-medium' 
                      : 'text-muted-foreground'
                  }`}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 flex items-center justify-between">
                <button
                  onClick={cycleTheme}
                  className="flex items-center gap-2 text-muted-foreground"
                  data-testid="mobile-theme-switcher"
                >
                  <Palette className="w-5 h-5 text-accent" />
                  <span className="text-sm">Tema: {currentThemeData.name}</span>
                </button>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
